import { CheckBoxWithLabelWidget,TextFieldWidget } from '@tableau/widgets';
import React from 'react';

import { Messages } from '~src/codegen/Localize';
import { ReactActionDialogBodyProps } from '~src/commonInfra/dialogs/ReactActionDialog';
import { IDatabaseConnectionAuthInfo } from '~src/connectivity/serverApi/types/connection/IConnectionAuthInfo';

export interface CheckDatabaseConnectionCredentialsProps {
    dialogTitle: string;
    dialogDescription: string;
}

export interface CheckDatabaseConnectionCredentialsState {
    connection: IDatabaseConnectionAuthInfo;
    errorMessage?: string;
}

type CheckDatabaseConnectionCredentialsReactActionProps = ReactActionDialogBodyProps<
    CheckDatabaseConnectionCredentialsProps,
    CheckDatabaseConnectionCredentialsState
>;

type ConnectionDetailsLabelProps = Readonly<{
    label: string;
}>;

type ConnectionDetailsValueProps = Readonly<{
    value: string;
}>;

type ConnectionDetailsRowProps = Readonly<{
    label: string;
    value: string;
}>;

function ConnectionDetailsLabel(props: ConnectionDetailsLabelProps) {
    return (
        <div className='tb-connection-details-label'>
            {props.label}
        </div>
    );
}

function ConnectionDetailsValue(props: ConnectionDetailsValueProps) {
    return (
        <div className='tb-connection-details-value'>
            {props.value}
        </div>
    );
}

function ConnectionDetailsRow(props: ConnectionDetailsRowProps) {
    return (
        <div className='tb-connection-details-row'>
            <ConnectionDetailsLabel
                label={props.label}
            />
            <ConnectionDetailsValue
                value={props.value}
            />
        </div>
    );
}

type ConnectionFormTextFieldProps = Readonly<{
    value: string;
    handleChange: (value: string) => void;
    handleEnterKey: () => void;
}>;

type ConnectionFormEmbedCredentialsProps = Readonly<{
    value: boolean;
    handleChange: (value: boolean) => void;
}>;

function ConnectionFormUsername(props: ConnectionFormTextFieldProps) {
    const className = 'tb-connection-credentials-username';
    return (
        <TextFieldWidget
            className={className}
            testId='connection-credential-username'
            onChange={e => props.handleChange(e.target.value)}
            value={props.value}
            label={Messages.username()}
            maxLength={255}
            style={{ width: '100%' }}
            onKeyDown={e => e.key === 'Enter' ? props.handleEnterKey() : undefined}
        />
    );
}

function ConnectionFormPassword(props: ConnectionFormTextFieldProps) {
    const className = 'tb-connection-credentials-password';
    return (
        <TextFieldWidget
            className={className}
            testId='connection-credential-password'
            onChange={e => props.handleChange(e.target.value)}
            value={props.value}
            label={Messages.password()}
            maxLength={255}
            type='password'
            style={{ width: '100%' }}
            onKeyDown={e => e.key === 'Enter' ? props.handleEnterKey() : undefined}
        />
    );
}

function ConnectionFormEmbedCredentials(props: ConnectionFormEmbedCredentialsProps) {
    return (
        <CheckBoxWithLabelWidget
            label={Messages.revisionHistoryRestoreEmbedCredentials()}
            checked={props.value}
            testId='connection-embed-credentials'
            handleChange={props.handleChange}
        />
    );
}

export function CheckDatabaseConnectionCredentialsDialog(props: CheckDatabaseConnectionCredentialsReactActionProps) {
    let connectionProp = props.dialogState.connection;
    return (
        <>
            <div className='tb-dialog-title tb-padded-top-large'>
                {props.dialogTitle}
            </div>
            <div className='tb-dialog-description'>
                {props.dialogDescription}
            </div>
            <div className='tb-connection-details'>
                <ConnectionDetailsRow
                    label={Messages.revisionHistoryCredentialsDialogTypeLabel()}
                    value={connectionProp.connectionTypeDisplayName}
                />
                <ConnectionDetailsRow
                    label={Messages.revisionHistoryCredentialsDialogServerLabel()}
                    value={connectionProp.server}
                />
                <ConnectionDetailsRow
                    label={Messages.revisionHistoryCredentialsDialogDatabaseLabel()}
                    value={connectionProp.dbname}
                />
            </div>
            <div className='tb-connection-credentials-form'>
                <ConnectionFormUsername
                    value={connectionProp.keychain.username}
                    handleChange={(value) => props.updateDialogState(
                        { connection: { ...connectionProp, keychain: { ...connectionProp.keychain, username: value } } })
                    }
                    handleEnterKey={props.doAction}
                />
                <ConnectionFormPassword
                    value={connectionProp.keychain.unencryptedPassword}
                    handleChange={(value) => props.updateDialogState(
                        { connection: { ...connectionProp, keychain: { ...connectionProp.keychain, unencryptedPassword: value } } })
                    }
                    handleEnterKey={props.doAction}
                />
                <ConnectionFormEmbedCredentials
                    value={connectionProp.keychain.embedCredentials}
                    handleChange={(value) => props.updateDialogState(
                        { connection: { ...connectionProp, keychain: { ...connectionProp.keychain, embedCredentials: value } } })
                    }
                />
            </div>
            <div className='tb-dialog-error'>{props.dialogState.errorMessage}</div>
        </>
    );
}
